/* Hallmark · redesign: home Workbench — split hero + live theme shelf + concrete reasons + data ownership · genre: editorial
 * theme: host_ui.css (design.md) · tone: premium product showroom
 * The how-it-works section is a captioned recording of the real product instead of an illustrated
 * approximation. Playback stays user-initiated behind a poster so the page never starts talking on its own.
 * The guest-view-coda now follows the hero as a swipeable carousel of real per-theme screenshots
 * (same example couple/content, five different themes) with a hover-scroll reveal on desktop (`@media (hover: hover)`,
 * Tailwind v4 default — untestable in this headless CI which reports `hover: none`, verified correct via
 * matched CSS selector instead) and native touch-scroll on mobile. The scroll track is full-bleed (can
 * reach the viewport edge as you swipe/scroll), but its resting `padding-left` mirrors the max-w-6xl
 * column's own centering math (`max(1rem, calc((var(--vw-safe,100vw)-72rem)/2+1rem))`) so card 1 starts
 * pixel-perfectly aligned with the heading above it at every viewport width, not flush against the
 * window edge. Two things had to be right for that: (1) `scroll-padding-left` must match the
 * `padding-left` exactly — without it, `scroll-snap-type` silently auto-scrolls the container on load
 * to cancel the padding out (card 1 ends up flush left regardless of the padding); (2) plain `100vw`
 * includes the scrollbar while `mx-auto` centering (used by the heading) doesn't, so they're off by
 * half the scrollbar width — fixed by measuring `document.documentElement.clientWidth` in a resize-aware
 * effect into the `--vw-safe` custom property, which the calc reads instead of raw `100vw`. The prev/next
 * controls sit in their own `mx-auto max-w-6xl px-4 sm:px-6 → max-w-2xl` box (the exact same structure as
 * the heading/paragraph), right-aligned within it — so they land on the *text* column's right edge, not
 * the wider carousel-track's edge, while the card track itself still bleeds past them to the viewport
 * edge. `pb-20 sm:pb-28` on the coda gives the controls room to breathe before the section's border.
 * The data-ownership section is a light storybook keepsake: a paper-like panel, watercolor
 * washes, a hand-drawn botanical seal, and four calm controls that make the privacy promise
 * feel like part of the invitation instead of a dark product disclaimer.
 * Pricing uses an asymmetric anchor + appendix: the 2fr dark Goldypaper plan owns the price,
 * value, and primary CTA; the 1fr Custom aside uses a soft paper surface and steps down in
 * type, density, and contact voice so it remains discoverable without competing.
 * Romance layer (2026-08): the hero plays. Deterministic golden petals drift through it
 * (CSS-only, translate-only, mid-flight via negative delays), gold sparkles trail the pointer
 * on fine pointers and burst wherever the hero is tapped (spawned from an effect, so SSR ships
 * an empty overlay and hydration matches), and the "Make it yours" name-play swaps the H1 to
 * the couple's own names with a one-time heart bloom around the ampersand. Everything
 * decorative is aria-hidden; prefers-reduced-motion stills all of it.
 * mobile: pass (pricing verified via Capybara screenshots at 320/375/414/768px and desktop at 1280px)
 * Hallmark · pre-emit critique: P5 H5 E5 S5 R5 V5
 */
import React from "react";
import { useContent } from "@thoughtbot/superglue";
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpRightIcon,
  CalendarClockIcon,
  ClipboardCheckIcon,
  DownloadIcon,
  EyeIcon,
  HeartIcon,
  LinkIcon,
  PlayIcon,
  SearchSlashIcon,
  ShieldCheckIcon,
  SparkleIcon,
  Trash2Icon,
  type LucideIcon,
} from "lucide-react";

import { BrandMark } from "@javascript/components/BrandMark";
import { MarketingFooter } from "@javascript/components/MarketingFooter";
import { Badge } from "@javascript/components/ui/badge";
import { Button } from "@javascript/components/ui/button";
import { cn } from "@javascript/components/utils";

type ThemePreview = {
  key: string;
  name: string;
  category: string | null;
  imagePath: string;
  imageWidth: number;
  imageHeight: number;
  demoPath: string;
};

type ProductWalkthroughAssets = {
  posterPath: string;
  webmPath: string;
  mp4Path: string;
  captionsPath: string;
};

type HomeContent = {
  signInPath: string;
  signUpPath: string;
  heroMotifPath: string;
  themePreviews: ThemePreview[];
  productWalkthrough: ProductWalkthroughAssets;
  price?: string;
};

const REASONS: Array<{ title: string; body: string; icon: LucideIcon }> = [
  {
    title: "Start with a point of view",
    body: "A curated collection of wedding-specific themes gives you a considered beginning, so the page feels designed before you add a single detail.",
    icon: SparkleIcon,
  },
  {
    title: "Plan more than one moment",
    body: "Put every event, venue, dress code, travel note, FAQ, and registry on the same invitation.",
    icon: CalendarClockIcon,
  },
  {
    title: "Make RSVP feel effortless",
    body: "Guests reply inline without an app or account. You see attendance, expected headcount, and every note in one dashboard.",
    icon: ClipboardCheckIcon,
  },
  {
    title: "Share one link with confidence",
    body: "Publish a mobile-first page with a clear address, and a preview carrying your names, date, and cover photo wherever the link is shared.",
    icon: LinkIcon,
  },
];

const DATA_CONTROLS: Array<{ title: string; body: string; icon: LucideIcon }> = [
  {
    title: "Download guest list",
    body: "Take a copy whenever you need one — a spreadsheet that opens straight in Google Sheets, Excel, or Numbers.",
    icon: DownloadIcon,
  },
  {
    title: "Request deletion anytime",
    body: "Email us when the celebration is over. Once confirmed, we permanently delete the invitation and guest data within 30 days.",
    icon: Trash2Icon,
  },
  {
    title: "No ads, no trackers",
    body: "No advertising or tracking scripts run on the invitation and RSVP page your guests open.",
    icon: ShieldCheckIcon,
  },
  {
    title: "Kept out of search results",
    body: "We never list your invitation with search engines, and every page asks them to skip it. The link still works for everyone you send it to.",
    icon: SearchSlashIcon,
  },
];

function HeroConfetti() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 64 48"
      fill="none"
      className="pointer-events-none absolute -right-3 -top-7 h-10 w-14 text-primary sm:-right-5 sm:-top-8 sm:h-12 sm:w-16"
    >
      <rect x="6" y="6" width="8" height="8" rx="2" fill="currentColor" opacity="0.85" transform="rotate(-18 10 10)" />
      <rect x="44" y="4" width="7" height="7" rx="2" fill="currentColor" opacity="0.5" transform="rotate(22 47.5 7.5)" />
      <circle cx="30" cy="10" r="2.5" fill="currentColor" opacity="0.9" />
      <circle cx="54" cy="24" r="2" fill="currentColor" opacity="0.6" />
      <circle cx="18" cy="30" r="2" fill="currentColor" opacity="0.4" />
      <path d="M36 26c4 2 6 6 4 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.55" />
    </svg>
  );
}

function prefersReducedMotion() {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

/* Deterministic, so the server render and hydration agree byte-for-byte; the
 * drift is CSS-only, so crawlers and no-JS visitors just see a still hero. */
const HERO_PETALS: Array<{
  left: number;
  size: number;
  duration: number;
  delay: number;
  drift: string;
  spin: string;
  opacity: number;
  light: boolean;
}> = [
  { left: 3, size: 14, duration: 30, delay: -4, drift: "3rem", spin: "320deg", opacity: 0.32, light: false },
  { left: 11, size: 10, duration: 24, delay: -18, drift: "-2.5rem", spin: "-240deg", opacity: 0.5, light: true },
  { left: 19, size: 12, duration: 33, delay: -9, drift: "2.5rem", spin: "280deg", opacity: 0.26, light: false },
  { left: 28, size: 9, duration: 26, delay: -22, drift: "-3rem", spin: "-300deg", opacity: 0.44, light: true },
  { left: 37, size: 15, duration: 31, delay: -14, drift: "3.5rem", spin: "340deg", opacity: 0.22, light: false },
  { left: 47, size: 11, duration: 28, delay: -2, drift: "-2rem", spin: "-260deg", opacity: 0.38, light: true },
  { left: 56, size: 13, duration: 34, delay: -25, drift: "2rem", spin: "300deg", opacity: 0.3, light: false },
  { left: 66, size: 10, duration: 25, delay: -11, drift: "-3.5rem", spin: "-280deg", opacity: 0.48, light: true },
  { left: 75, size: 12, duration: 30, delay: -19, drift: "2.5rem", spin: "260deg", opacity: 0.34, light: false },
  { left: 84, size: 9, duration: 27, delay: -6, drift: "-2rem", spin: "-320deg", opacity: 0.42, light: true },
  { left: 92, size: 13, duration: 32, delay: -27, drift: "3rem", spin: "300deg", opacity: 0.28, light: false },
];

function HeroPetals() {
  return (
    <div
      data-hero-petals
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {HERO_PETALS.map((petal) => (
        <svg
          key={petal.left}
          viewBox="0 0 20 24"
          fill="none"
          className={`landing-petal${petal.light ? " landing-petal-light" : ""}`}
          style={
            {
              left: `${petal.left}%`,
              width: petal.size,
              height: petal.size * 1.2,
              "--petal-duration": `${petal.duration}s`,
              "--petal-delay": `${petal.delay}s`,
              "--petal-drift": petal.drift,
              "--petal-spin": petal.spin,
              "--petal-opacity": petal.opacity,
            } as React.CSSProperties
          }
        >
          <path
            d="M10 .8C7 3.6 3.2 7.2 3.2 11.6a6.8 6.8 0 0 0 13.6 0C16.8 7.2 13 3.6 10 .8Z"
            fill="currentColor"
          />
        </svg>
      ))}
    </div>
  );
}

/* Gold twinkles trailing the pointer on desktop and bursting wherever the
 * hero is tapped on touch. Everything lives in an effect and in event
 * handlers, so the server render ships an empty overlay and hydration
 * matches. Reduced-motion visitors get none of it. */
type Sparkle = { id: number; x: number; y: number; size: number; rotation: number };

const SPARKLE_LIFETIME_MS = 750;
const SPARKLE_TRAIL_THROTTLE_MS = 90;
const SPARKLE_BURST_COUNT = 3;
const SPARKLE_MAX = 14;

function HeroSparkles() {
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const [sparkles, setSparkles] = React.useState<Sparkle[]>([]);
  const nextSparkleIdRef = React.useRef(0);
  const lastTrailAtRef = React.useRef(0);
  const removalTimersRef = React.useRef<number[]>([]);

  React.useEffect(() => {
    const hero = overlayRef.current?.closest<HTMLElement>("[data-section='hero']");
    if (!hero || prefersReducedMotion()) return;

    const finePointer = window.matchMedia?.("(hover: hover) and (pointer: fine)").matches ?? false;

    const spawn = (x: number, y: number) => {
      nextSparkleIdRef.current += 1;
      const id = nextSparkleIdRef.current;
      setSparkles((current) =>
        [...current, { id, x, y, size: 6 + Math.random() * 7, rotation: Math.random() * 90 }].slice(
          -SPARKLE_MAX,
        ),
      );

      const timer = window.setTimeout(() => {
        setSparkles((current) => current.filter((sparkle) => sparkle.id !== id));
        removalTimersRef.current = removalTimersRef.current.filter((pending) => pending !== timer);
      }, SPARKLE_LIFETIME_MS);
      removalTimersRef.current.push(timer);
    };

    const onPointerMove = (event: PointerEvent) => {
      const now = Date.now();
      if (now - lastTrailAtRef.current < SPARKLE_TRAIL_THROTTLE_MS) return;
      lastTrailAtRef.current = now;
      const rect = hero.getBoundingClientRect();
      spawn(event.clientX - rect.left, event.clientY - rect.top);
    };

    const onPointerDown = (event: PointerEvent) => {
      const rect = hero.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      for (let index = 0; index < SPARKLE_BURST_COUNT; index += 1) {
        spawn(x + (Math.random() - 0.5) * 28, y + (Math.random() - 0.5) * 28);
      }
    };

    if (finePointer) hero.addEventListener("pointermove", onPointerMove);
    hero.addEventListener("pointerdown", onPointerDown);

    return () => {
      if (finePointer) hero.removeEventListener("pointermove", onPointerMove);
      hero.removeEventListener("pointerdown", onPointerDown);
      removalTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      removalTimersRef.current = [];
    };
  }, []);

  return (
    <div
      ref={overlayRef}
      data-hero-sparkles
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-20 overflow-hidden"
    >
      {sparkles.map((sparkle) => (
        <span
          key={sparkle.id}
          data-hero-sparkle
          className="landing-sparkle"
          style={
            {
              left: sparkle.x,
              top: sparkle.y,
              "--sparkle-size": `${sparkle.size}px`,
              "--sparkle-rotation": `${sparkle.rotation}deg`,
            } as React.CSSProperties
          }
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" className="block size-full">
            <path
              d="M12 2c.9 5.2 4.8 9.1 10 10-5.2.9-9.1 4.8-10 10-.9-5.2-4.8-9.1-10-10 5.2-.9 9.1-4.8 10-10Z"
              fill="currentColor"
            />
          </svg>
        </span>
      ))}
    </div>
  );
}

/* Hearts burst once around the ampersand the moment both names arrive —
 * the reward moment of the name-play. */
const HEART_BLOOM_OFFSETS = [0, 60, 120, 180, 240, 300].map((degrees) => {
  const radians = (degrees * Math.PI) / 180;
  return {
    x: `${(Math.cos(radians) * 2.4).toFixed(2)}rem`,
    y: `${(Math.sin(radians) * 2.4).toFixed(2)}rem`,
  };
});

function HeartBloom() {
  return (
    <span aria-hidden="true" className="pointer-events-none absolute inset-0">
      {HEART_BLOOM_OFFSETS.map((offset) => (
        <HeartIcon
          key={`${offset.x}-${offset.y}`}
          fill="currentColor"
          strokeWidth={0}
          style={{ "--bloom-x": offset.x, "--bloom-y": offset.y } as React.CSSProperties}
          className="landing-bloom-heart absolute left-1/2 top-1/2 size-3 text-primary"
        />
      ))}
    </span>
  );
}

type CoupleNames = { one: string; two: string };

function NamePlay({ names, onNamesChange }: { names: CoupleNames; onNamesChange: (next: CoupleNames) => void }) {
  const complete = names.one.trim() !== "" && names.two.trim() !== "";
  const [bloomKey, setBloomKey] = React.useState(0);
  const wasCompleteRef = React.useRef(false);

  React.useEffect(() => {
    if (complete && !wasCompleteRef.current && !prefersReducedMotion()) {
      setBloomKey((key) => key + 1);
    }
    wasCompleteRef.current = complete;
  }, [complete]);

  return (
    <div
      data-name-play
      className="landing-reveal mt-8 max-w-xl"
      style={{ "--reveal-index": 4 } as React.CSSProperties}
    >
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-background/55">
        Make it yours
      </p>
      <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-2">
        <label htmlFor="landing-couple-name-one" className="sr-only">
          Your name
        </label>
        <input
          id="landing-couple-name-one"
          type="text"
          value={names.one}
          maxLength={40}
          autoComplete="off"
          placeholder="Aiko"
          onChange={(event) => onNamesChange({ ...names, one: event.target.value })}
          className="w-32 border-b border-background/30 bg-transparent pb-1.5 font-serif text-xl italic leading-normal text-background placeholder:font-normal placeholder:not-italic placeholder:text-background/40 focus:border-primary focus:outline-none sm:w-40"
        />
        <span
          aria-hidden="true"
          className="relative inline-flex font-serif text-2xl italic leading-normal text-primary"
        >
          &amp;
          {bloomKey > 0 && <HeartBloom key={bloomKey} />}
        </span>
        <label htmlFor="landing-couple-name-two" className="sr-only">
          Their name
        </label>
        <input
          id="landing-couple-name-two"
          type="text"
          value={names.two}
          maxLength={40}
          autoComplete="off"
          placeholder="Sean"
          onChange={(event) => onNamesChange({ ...names, two: event.target.value })}
          className="w-32 border-b border-background/30 bg-transparent pb-1.5 font-serif text-xl italic leading-normal text-background placeholder:font-normal placeholder:not-italic placeholder:text-background/40 focus:border-primary focus:outline-none sm:w-40"
        />
      </div>
      <p aria-live="polite" className="mt-3 text-sm leading-relaxed text-background/55">
        {complete ? "Now it’s your invitation." : "Type both names — the headline becomes yours."}
      </p>
    </div>
  );
}

function ReasonsOrnament() {
  return (
    <svg
      data-reasons-ornament
      aria-hidden
      viewBox="0 0 280 280"
      fill="none"
      className="landing-reasons-ornament pointer-events-none absolute -bottom-24 -right-20 size-72 text-primary/30 sm:-bottom-32 sm:-right-24 sm:size-[26rem]"
    >
      <circle cx="140" cy="140" r="104" stroke="currentColor" strokeWidth="0.8" />
      <circle
        cx="140"
        cy="140"
        r="82"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeDasharray="1 8"
        strokeLinecap="round"
      />
      <path
        d="M140 16v42M140 222v42M16 140h42M222 140h42"
        stroke="currentColor"
        strokeWidth="0.8"
      />
      <path
        d="M140 72c-24 0-44 20-44 44 0 24 20 44 44 44s44-20 44-44c0-24-20-44-44-44Z"
        stroke="currentColor"
        strokeWidth="0.8"
      />
      <circle cx="140" cy="116" r="7" fill="currentColor" fillOpacity="0.45" />
      <circle cx="140" cy="116" r="12" stroke="currentColor" strokeWidth="0.8" />
    </svg>
  );
}

function DataOwnershipOrnament() {
  return (
    <>
      <svg
        data-data-ownership-ornament
        aria-hidden
        viewBox="0 0 440 400"
        fill="none"
        className="landing-data-ownership-ornament pointer-events-none absolute -right-40 -top-32 h-[26rem] w-[30rem] text-primary/35 sm:-right-28 sm:-top-28 sm:h-[32rem] sm:w-[36rem]"
      >
        <ellipse
          cx="312"
          cy="160"
          rx="118"
          ry="82"
          transform="rotate(-18 312 160)"
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.55"
        />
        <ellipse
          cx="312"
          cy="160"
          rx="92"
          ry="122"
          transform="rotate(28 312 160)"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeDasharray="2 11"
          strokeLinecap="round"
          opacity="0.48"
        />
        <path
          d="M218 346c16-94 73-173 166-223"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          opacity="0.7"
        />
        <path
          d="M286 247c-33-22-64-21-92-5 21 31 52 37 89 14Z"
          fill="currentColor"
          fillOpacity="0.14"
        />
        <path
          d="M329 190c-7-36 9-63 37-82 12 31-1 58-31 81Z"
          fill="currentColor"
          fillOpacity="0.16"
        />
        <path
          d="M343 276c26-17 52-17 75-5-16 25-42 31-73 16Z"
          fill="currentColor"
          fillOpacity="0.12"
        />
        <path
          d="M337 117c-18-23-54-8-46 21 7 18 34 35 46 43 12-8 39-25 46-43 8-29-28-44-46-21Z"
          fill="currentColor"
          fillOpacity="0.07"
          stroke="currentColor"
          strokeWidth="1.3"
        />
        <path
          d="M397 68l4 11 11 4-11 4-4 11-4-11-11-4 11-4Z"
          fill="currentColor"
          fillOpacity="0.5"
        />
        <path
          d="M244 91l3 8 8 3-8 3-3 8-3-8-8-3 8-3Z"
          fill="currentColor"
          fillOpacity="0.35"
        />
        <circle cx="274" cy="51" r="4" fill="currentColor" fillOpacity="0.55" />
        <circle cx="391" cy="229" r="3" fill="currentColor" fillOpacity="0.65" />
      </svg>
      <svg
        aria-hidden
        viewBox="0 0 360 260"
        fill="none"
        className="landing-data-ownership-echo pointer-events-none absolute -bottom-20 -left-20 h-64 w-[22rem] text-success/25 sm:-bottom-24 sm:-left-16 sm:h-72 sm:w-[26rem]"
      >
        <path
          d="M-12 206c72-56 134-63 206-22 49 28 94 26 178-22"
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinecap="round"
          opacity="0.7"
        />
        <path
          d="M20 221c71-49 128-54 192-19 48 26 96 24 164-13"
          stroke="currentColor"
          strokeWidth="1"
          strokeDasharray="2 10"
          strokeLinecap="round"
          opacity="0.55"
        />
        <path
          d="M118 185c-6-28 5-51 28-68 9 25-1 48-25 67Z"
          fill="currentColor"
          fillOpacity="0.15"
        />
        <path
          d="M156 161c20-21 43-25 65-16-9 24-30 35-61 27Z"
          fill="currentColor"
          fillOpacity="0.12"
        />
        <path
          d="M155 237c2-53 18-91 49-119"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.6"
        />
        <circle cx="72" cy="183" r="3" fill="currentColor" fillOpacity="0.55" />
        <circle cx="250" cy="203" r="4" fill="currentColor" fillOpacity="0.35" />
      </svg>
    </>
  );
}

function ReasonsSection({ themeCount }: { themeCount: number }) {
  return (
    <section
      id="why-goldypaper"
      data-section="reasons"
      aria-labelledby="reasons-heading"
      className="border-t bg-background"
    >
      <div className="mx-auto w-full max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-stretch lg:gap-16">
          <div
            data-reasons-thesis
            className="landing-reasons-thesis relative min-w-0 overflow-hidden rounded-2xl border bg-foreground p-7 text-background sm:p-10"
          >
            <ReasonsOrnament />
            <div className="relative z-10 flex min-h-[26rem] flex-col">
              <header className="max-w-md">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">
                  Why Goldypaper
                </p>
                <h2
                  id="reasons-heading"
                  className="mt-5 min-w-0 font-serif text-4xl font-semibold leading-[1.05] tracking-tight text-background [overflow-wrap:anywhere] sm:text-5xl"
                >
                  The part your guests remember.
                </h2>
                <p className="mt-6 text-lg leading-relaxed text-background/70">
                  Most wedding tools begin with a dashboard. Goldypaper begins with the
                  moment someone opens your link: a clear, beautiful place that makes the
                  day easier to understand and more yours to share.
                </p>
              </header>

              <div className="mt-auto border-t border-background/20 pt-5 text-sm text-background/65">
                <span className="inline-flex items-center gap-2 font-semibold text-background">
                  <EyeIcon aria-hidden strokeWidth={1.75} className="size-4 text-primary" />
                  Guest-first by design
                </span>
              </div>
            </div>
          </div>

          <div data-reasons-grid data-reasons-list className="min-w-0 border-y border-border">
            {REASONS.map(({ title, body, icon: Icon }) => (
              <article
                key={title}
                data-reason-card
                className="landing-reason-card flex min-w-0 gap-4 border-b border-border px-4 py-7 last:border-b-0 sm:gap-5 sm:px-6 sm:py-8 lg:px-8"
              >
                <span className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-full border border-primary/35 bg-primary/10 text-primary">
                  <Icon aria-hidden strokeWidth={1.5} className="size-5" />
                </span>
                <div className="min-w-0">
                  <h3 className="min-w-0 font-serif text-2xl font-semibold leading-tight [overflow-wrap:anywhere]">
                    {title}
                  </h3>
                  <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                    {body}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>

        <dl
          data-proof-rail
          className="mt-16 grid border-y border-border sm:grid-cols-4"
        >
          <div data-proof-item className="border-b px-4 py-5 sm:border-b-0 sm:border-r sm:px-5">
            <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Starting points
            </dt>
            <dd className="mt-2 font-serif text-2xl font-semibold tabular-nums">
              {themeCount} curated themes
            </dd>
          </div>
          <div data-proof-item className="border-b px-4 py-5 sm:border-b-0 sm:border-r sm:px-5">
            <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              For guests
            </dt>
            <dd className="mt-2 font-serif text-2xl font-semibold">One clear link</dd>
          </div>
          <div data-proof-item className="border-b px-4 py-5 sm:border-b-0 sm:border-r sm:px-5">
            <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Built in
            </dt>
            <dd className="mt-2 font-serif text-2xl font-semibold">Inline RSVP</dd>
          </div>
          <div data-proof-item className="px-4 py-5 sm:px-5">
            <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Guest capacity
            </dt>
            <dd className="mt-2 font-serif text-2xl font-semibold">Unlimited</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

function DataOwnershipSection() {
  return (
    <section
      id="data-ownership"
      data-section="data-ownership"
      aria-labelledby="data-ownership-heading"
      className="landing-data-section border-t border-primary/20 bg-primary/10"
    >
      <div className="mx-auto w-full max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
        <div
          data-data-ownership-panel
          className="landing-data-story relative isolate overflow-hidden rounded-[2.5rem] border border-primary/35 px-6 py-9 text-foreground shadow-xl sm:px-10 sm:py-12 lg:px-14 lg:py-14"
        >
          <div aria-hidden className="landing-data-glow pointer-events-none absolute inset-0" />
          <DataOwnershipOrnament />

          <div className="relative z-10 grid gap-10 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:items-center lg:gap-16">
            <div data-data-ownership-story className="flex min-w-0 flex-col">
              <span
                data-data-ownership-badge
                className="inline-flex w-fit items-center gap-2 rounded-full border border-primary/35 bg-primary/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-primary"
              >
                <HeartIcon aria-hidden fill="currentColor" strokeWidth={1.5} className="size-3.5" />
                A quiet promise
              </span>

              <header className="mt-6 max-w-xl lg:max-w-md">
                <h2
                  id="data-ownership-heading"
                  className="min-w-0 font-serif text-4xl font-semibold leading-[1.05] tracking-tight text-foreground [overflow-wrap:anywhere] sm:text-5xl"
                >
                  Your wedding, your data.
                </h2>
                <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                  Your guest list holds the little things that make the day yours. Keep every
                  name, reply, and note close — with a clear say in what stays and what goes.
                </p>
              </header>

              <div className="mt-8 flex max-w-md items-start gap-3 border-t border-primary/25 pt-5">
                <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary">
                  <SparkleIcon aria-hidden strokeWidth={1.5} className="size-4" />
                </span>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  A celebration should leave memories, not a trail. Goldypaper keeps the
                  details close to the people they belong to.
                </p>
              </div>
            </div>

            <div
              data-data-controls
              className="grid gap-4 sm:grid-cols-2"
            >
              {DATA_CONTROLS.map(({ title, body, icon: Icon }) => (
                <article
                  key={title}
                  data-data-control
                  className="landing-data-card group relative flex min-w-0 flex-col rounded-2xl border border-primary/20 p-5 sm:p-6"
                >
                  <span className="landing-data-card-icon mb-5 flex size-10 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary">
                    <Icon aria-hidden strokeWidth={1.45} className="size-5" />
                  </span>
                  <h3 className="font-serif text-xl font-semibold leading-snug text-foreground">
                    {title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
                </article>
              ))}
            </div>
          </div>

          <div
            data-data-ownership-note
            className="relative z-10 mt-10 flex max-w-2xl items-start gap-3 border-t border-primary/25 pt-5 text-sm leading-relaxed text-muted-foreground"
          >
            <ShieldCheckIcon
              aria-hidden
              strokeWidth={1.6}
              className="mt-0.5 size-4 shrink-0 text-primary"
            />
            <p>Your celebration is yours to keep — and yours to carry forward.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductWalkthrough({ assets }: { assets: ProductWalkthroughAssets }) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [started, setStarted] = React.useState(false);

  const startPlayback = () => {
    const video = videoRef.current;
    if (!video) return;

    setStarted(true);
    const playback = video.play();
    playback?.catch(() => undefined);
  };

  return (
    <div
      data-product-walkthrough
      className="mt-12 overflow-hidden rounded-2xl border border-border bg-foreground shadow-xl sm:mt-14"
    >
      <div className="relative aspect-video">
        <video
          ref={videoRef}
          aria-label="Goldypaper product walkthrough"
          className="size-full bg-foreground object-cover"
          controls={started}
          playsInline
          poster={assets.posterPath}
          preload="metadata"
          onPlay={() => setStarted(true)}
        >
          <source src={assets.webmPath} type="video/webm" />
          <source src={assets.mp4Path} type="video/mp4" />
          <track
            kind="captions"
            label="English"
            src={assets.captionsPath}
            srcLang="en"
          />
          Your browser does not support embedded video.
        </video>

        {!started && (
          <button
            type="button"
            aria-label="Play the Goldypaper product walkthrough"
            className="group absolute inset-0 flex items-center justify-center bg-foreground/10 text-background transition-colors hover:bg-foreground/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            onClick={startPlayback}
          >
            <span className="flex size-20 items-center justify-center rounded-full border border-background/40 bg-foreground/85 shadow-2xl transition-transform group-hover:scale-105 motion-reduce:transform-none sm:size-24">
              <PlayIcon
                aria-hidden
                fill="currentColor"
                strokeWidth={1.5}
                className="ml-1 size-8 sm:size-10"
              />
            </span>
            <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/90 to-transparent px-5 pt-16 pb-5 text-left sm:px-8 sm:pb-7">
              <span className="block text-sm font-semibold sm:text-base">Watch the walkthrough</span>
              <span className="mt-1 block text-xs text-background/70 sm:text-sm">
                Theme to first RSVP · under one minute
              </span>
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

const THEME_FIT_DESCRIPTIONS: Record<string, string> = {
  Classic: "For the timeless, black-tie kind of romantic.",
  Bold: "For couples who want their wedding to feel like a celebration.",
  Romantic: "For hopeless romantics who want every detail to say it.",
  Natural: "For outdoor souls and garden-party dreamers.",
  Minimal: "For couples who’d rather let their story speak for itself.",
  Harvest: "For couples who believe the best celebrations begin around a shared table.",
};
const DEFAULT_FIT_DESCRIPTION = "A theme designed with just as much care.";

function hoverScrollDuration({ imageWidth, imageHeight }: ThemePreview) {
  return `${Math.round((imageHeight / imageWidth) * 1500)}ms`;
}

function ThemePreviewCarousel({ previews }: { previews: ThemePreview[] }) {
  const scrollerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const updateScrollbarSafeWidth = () => {
      document.documentElement.style.setProperty(
        "--vw-safe",
        `${document.documentElement.clientWidth}px`,
      );
    };
    updateScrollbarSafeWidth();
    window.addEventListener("resize", updateScrollbarSafeWidth);
    return () => window.removeEventListener("resize", updateScrollbarSafeWidth);
  }, []);

  const scrollByCard = (direction: 1 | -1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const card = scroller.querySelector<HTMLElement>("[data-theme-preview]");
    const amount = (card?.offsetWidth ?? 280) + 16;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    scroller.scrollBy({ left: amount * direction, behavior: prefersReducedMotion ? "auto" : "smooth" });
  };

  return (
    <div className="mt-8">
      <div
        ref={scrollerRef}
        data-theme-preview-carousel
        role="region"
        aria-label="Theme previews"
        className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 pl-[max(1rem,calc((var(--vw-safe,100vw)-72rem)/2+1rem))] pr-4 scroll-pl-[max(1rem,calc((var(--vw-safe,100vw)-72rem)/2+1rem))] sm:pl-[max(1.5rem,calc((var(--vw-safe,100vw)-72rem)/2+1.5rem))] sm:pr-6 sm:scroll-pl-[max(1.5rem,calc((var(--vw-safe,100vw)-72rem)/2+1.5rem))] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {previews.map((preview) => (
          <article
            key={preview.key}
            data-theme-preview={preview.key}
            className="group/preview w-64 shrink-0 snap-start sm:w-72"
          >
            <div className="group relative h-72 overflow-hidden rounded-xl border bg-muted shadow-sm sm:h-96">
              <img
                src={preview.imagePath}
                alt={`${preview.name} theme, shown on a published invitation page`}
                style={{ "--preview-scroll-duration": hoverScrollDuration(preview) } as React.CSSProperties}
                className="w-full transition-transform ease-linear [transition-duration:var(--preview-scroll-duration)] group-hover:-translate-y-[70%] motion-reduce:transition-none motion-reduce:group-hover:translate-y-0"
              />
            </div>
            <div className="mt-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-serif text-lg font-semibold">{preview.name}</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {THEME_FIT_DESCRIPTIONS[preview.category ?? ""] ?? DEFAULT_FIT_DESCRIPTION}
                </p>
              </div>
              <a
                data-theme-demo
                href={preview.demoPath}
                target="_blank"
                rel="noreferrer"
                aria-label={`View live ${preview.name} demo`}
                className="inline-flex shrink-0 items-center gap-1 pt-0.5 text-sm font-semibold text-primary underline decoration-primary/40 underline-offset-4 transition-colors hover:text-primary/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                View live demo
                <ArrowUpRightIcon aria-hidden className="size-3.5" />
              </a>
            </div>
          </article>
        ))}
      </div>
      <div className="mt-6 mx-auto w-full max-w-6xl px-4 sm:px-6">
        <div className="flex max-w-2xl justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Previous theme"
            onClick={() => scrollByCard(-1)}
          >
            <ArrowLeftIcon aria-hidden className="size-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Next theme"
            onClick={() => scrollByCard(1)}
          >
            <ArrowRightIcon aria-hidden className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { signInPath, signUpPath, heroMotifPath, themePreviews, productWalkthrough, price } =
    useContent<HomeContent>();
  const featuredTheme = themePreviews[0];
  const [coupleNames, setCoupleNames] = React.useState<CoupleNames>({ one: "", two: "" });
  const coupleComplete = coupleNames.one.trim() !== "" && coupleNames.two.trim() !== "";

  return (
    <main className="min-h-screen bg-background text-foreground">
      {price ? (
        <div
          data-landing-promo
          className="border-b border-primary/25 bg-primary/10 text-primary"
        >
          <p className="mx-auto w-full max-w-6xl px-4 py-2.5 text-center text-sm font-medium sm:px-6">
            ✨ {price} launch price (usually US$39) — no subscription, and every guest RSVPs for free.
          </p>
        </div>
      ) : null}

      <nav
        data-landing-nav
        aria-label="Main"
        className="landing-nav sticky top-0 z-30 border-b border-border/80 bg-background/90 backdrop-blur-md"
      >
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <a href="/" className="font-serif text-xl font-semibold tracking-tight">
            <BrandMark size="md" className="text-xl" />
          </a>
          <div data-nav-links className="hidden items-center gap-7 text-sm font-medium lg:flex">
            <a href="#themes" className="transition-colors hover:text-primary">
              Themes
            </a>
            <a href="#why-goldypaper" className="transition-colors hover:text-primary">
              Why Goldypaper
            </a>
            <a href="#how-it-works" className="transition-colors hover:text-primary">
              How it works
            </a>
            <a href="#pricing" className="transition-colors hover:text-primary">
              Pricing
            </a>
            <a href="/blog" className="transition-colors hover:text-primary">
              Blog
            </a>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Button asChild variant="ghost" size="sm">
              <a href={signInPath}>Sign in</a>
            </Button>
            <Button asChild size="sm" className="rounded-full px-4">
              <a href={signUpPath}>Get started</a>
            </Button>
          </div>
        </div>
      </nav>

      <section
        data-section="hero"
        className="landing-hero relative overflow-hidden bg-foreground text-background"
      >
        <div aria-hidden className="landing-hero-glow pointer-events-none absolute inset-0" />
        <HeroPetals />
        <HeroSparkles />

        <div className="relative mx-auto grid w-full max-w-6xl items-center gap-14 px-4 pb-16 pt-12 sm:px-6 sm:pb-24 sm:pt-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start lg:gap-16">
          <div className="min-w-0">
            <span
              className="landing-reveal inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-primary"
              style={{ "--reveal-index": 0 } as React.CSSProperties}
            >
              <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-primary" />
              {price ? `${price} once, per invitation` : "Once, per invitation"}
            </span>

            <h1
              className="landing-reveal mt-6 max-w-xl font-serif text-5xl font-semibold leading-[0.98] tracking-tight text-balance [overflow-wrap:anywhere] sm:text-7xl"
              style={{ "--reveal-index": 1 } as React.CSSProperties}
            >
              Premium invitations, made for{" "}
              {coupleComplete ? (
                <>
                  <span className="text-primary italic">{coupleNames.one.trim()}</span>
                  {" & "}
                  <span className="text-primary italic">{coupleNames.two.trim()}</span>’s big day.
                </>
              ) : (
                "your big day."
              )}
            </h1>

            <p
              className="landing-reveal mt-7 max-w-xl text-lg leading-relaxed text-background/70 sm:text-xl"
              style={{ "--reveal-index": 2 } as React.CSSProperties}
            >
              A page your guests actually want to open — the story, the schedule,
              the map, and an RSVP they can finish in a tap.
            </p>

            <div
              className="landing-reveal mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center"
              style={{ "--reveal-index": 3 } as React.CSSProperties}
            >
              <div className="relative">
                <HeroConfetti />
                <Button
                  asChild
                  size="lg"
                  data-cta="primary"
                  className="h-14 rounded-full px-8 text-base font-semibold shadow-lg shadow-primary/25 transition-transform duration-200 ease-out hover:-translate-y-0.5"
                >
                  <a href={signUpPath}>Start your invitation</a>
                </Button>
              </div>
              <a
                data-cta="secondary"
                href="#themes"
                className="inline-flex min-h-11 items-center gap-2 px-1 text-sm font-semibold text-background underline decoration-primary underline-offset-8 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-foreground"
              >
                Explore the themes
                <ArrowDownIcon aria-hidden className="size-4" />
              </a>
            </div>

            <NamePlay names={coupleNames} onNamesChange={setCoupleNames} />

            <dl
              data-hero-proof
              className="landing-reveal mt-12 grid max-w-xl grid-cols-2 gap-x-6 gap-y-5 border-t border-background/20 pt-5 sm:grid-cols-3"
              style={{ "--reveal-index": 4 } as React.CSSProperties}
            >
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-background/50">
                  For guests
                </dt>
                <dd className="mt-1 text-sm font-medium">No app or account</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-background/50">
                  Included
                </dt>
                <dd className="mt-1 text-sm font-medium">RSVP + calendar + maps</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-background/50">
                  Yours to share
                </dt>
                <dd className="mt-1 text-sm font-medium">One clear link</dd>
              </div>
            </dl>
          </div>

          <div
            className="landing-reveal landing-hero-art relative mx-auto w-full max-w-[30rem] lg:justify-self-end"
            style={{ "--reveal-index": 4 } as React.CSSProperties}
          >
            <img
              src={heroMotifPath}
              alt="Two interlocking gold wedding rings"
              width={440}
              height={440}
              loading="eager"
              decoding="async"
              className="landing-hero-rings pointer-events-none absolute -right-8 -top-10 z-10 h-auto w-28 sm:-right-12 sm:-top-14 sm:w-40"
            />
            {featuredTheme && (
              <figure data-hero-preview className="relative z-0 ml-auto w-[82%]">
                <div className="landing-hero-screenshot overflow-hidden rounded-[1.25rem] border border-background/20 bg-background/10 p-2 shadow-2xl shadow-background/10">
                  <div
                    data-hero-invitation-stage
                    tabIndex={0}
                    role="group"
                    aria-label={`Invitation preview for ${featuredTheme.name}. Hover or focus to open the invitation.`}
                    className="landing-hero-invitation-stage relative h-[28rem] overflow-hidden rounded-[0.8rem] bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-foreground sm:h-[36rem]"
                  >
                    <img
                      data-hero-invitation
                      src={featuredTheme.imagePath}
                      alt={`${featuredTheme.name} theme, shown on a published invitation page`}
                      width={featuredTheme.imageWidth}
                      height={featuredTheme.imageHeight}
                      loading="eager"
                      decoding="async"
                      style={{ "--hero-scroll-duration": hoverScrollDuration(featuredTheme) } as React.CSSProperties}
                      className="landing-hero-invitation block h-auto w-full max-w-none"
                    />
                    <span
                      aria-hidden="true"
                      className="landing-hero-invitation-hint pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center bg-gradient-to-t from-foreground/80 to-transparent px-4 pb-4 pt-10 text-center text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-background"
                    >
                      Open the invitation
                    </span>
                  </div>
                </div>
                <figcaption className="mt-4 flex items-center justify-between gap-3 text-sm">
                  <div>
                    <p className="font-semibold">Live guest view</p>
                    <p className="mt-1 text-background/55">
                      {featuredTheme.name} · {featuredTheme.category}
                    </p>
                  </div>
                  <a
                    href={featuredTheme.demoPath}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Open ${featuredTheme.name} live demo`}
                    className="inline-flex min-h-11 shrink-0 items-center gap-1.5 font-semibold text-primary underline decoration-primary/60 underline-offset-4 transition-colors hover:text-primary/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-foreground"
                  >
                    Open demo
                    <ArrowUpRightIcon aria-hidden className="size-4" />
                  </a>
                </figcaption>
              </figure>
            )}
          </div>
        </div>
      </section>

      <section
        id="themes"
        data-section="themes"
        data-guest-view-coda
        aria-labelledby="theme-showcase-heading"
        className="border-t border-border bg-[color-mix(in_oklab,var(--muted)_50%,var(--background))] pt-16 pb-20 sm:pt-24 sm:pb-28"
      >
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
          <div className="flex max-w-4xl flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">
                The gallery
              </p>
              <h3
                id="theme-showcase-heading"
                className="mt-4 min-w-0 font-serif text-4xl font-semibold leading-[1.05] tracking-tight [overflow-wrap:anywhere] sm:text-5xl"
              >
                Every theme, designed like it’s the only wedding that matters.
              </h3>
              <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
                Each one is crafted in-house, detail by detail. Browse the real guest view,
                then open the live demo before you decide where your story belongs.
              </p>
            </div>
            <a
              href="#why-goldypaper"
              className="inline-flex min-h-11 shrink-0 items-center gap-2 text-sm font-semibold text-primary underline decoration-primary/40 underline-offset-4 transition-colors hover:text-primary/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              See why it feels different
              <ArrowDownIcon aria-hidden className="size-4" />
            </a>
          </div>
        </div>
        <ThemePreviewCarousel previews={themePreviews} />
      </section>

      <ReasonsSection themeCount={themePreviews.length} />

      <section
        id="how-it-works"
        data-section="how-it-works"
        aria-labelledby="how-it-works-heading"
        className="border-t bg-muted/50"
      >
        <div
          data-how-it-works-content
          className="mx-auto w-full max-w-6xl px-4 pt-20 pb-20 sm:px-6 sm:pt-28 sm:pb-28"
        >
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(18rem,0.8fr)] lg:items-end lg:gap-14">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">
                Product walkthrough
              </p>
              <h2
                id="how-it-works-heading"
                className="mt-4 min-w-0 font-serif text-4xl font-semibold leading-tight tracking-tight [overflow-wrap:anywhere] sm:text-5xl"
              >
                See how Goldypaper works.
              </h2>
            </div>
            <p className="text-lg leading-relaxed text-muted-foreground">
              Watch a real invitation take shape — choose a theme, add the essentials,
              preview the guest view, pay once when you are ready, and publish one link
              that brings every RSVP home.
            </p>
          </div>

          <ProductWalkthrough assets={productWalkthrough} />

          <div className="mt-8 flex flex-col gap-6 border-t border-border pt-7 sm:flex-row sm:items-center sm:justify-between">
            <ol className="grid gap-3 text-sm font-medium text-muted-foreground sm:grid-cols-3 sm:gap-7">
              <li>
                <span className="mr-2 font-mono text-primary">01</span>
                Choose your theme
              </li>
              <li>
                <span className="mr-2 font-mono text-primary">02</span>
                Make every detail yours
              </li>
              <li>
                <span className="mr-2 font-mono text-primary">03</span>
                Publish and collect RSVPs
              </li>
            </ol>
            <Button asChild className="w-full shrink-0 sm:w-auto">
              <a href={signUpPath}>Start your invitation</a>
            </Button>
          </div>
        </div>
      </section>

      <DataOwnershipSection />

      <section
        id="pricing"
        data-section="pricing"
        aria-labelledby="pricing-heading"
        className="border-t bg-background"
      >
        <div className="mx-auto w-full max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
          <div className="max-w-2xl">
            <h2
              id="pricing-heading"
              className="min-w-0 font-serif text-4xl font-semibold leading-tight tracking-tight [overflow-wrap:anywhere] sm:text-5xl"
            >
              One price, per invitation.
            </h2>
            <p
              data-pricing-intro
              className="mt-5 text-lg leading-relaxed text-muted-foreground"
            >
              Pick any theme, publish your invitation, and keep every reply in
              one place — paid once, kept forever.
            </p>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(16rem,1fr)] lg:items-stretch lg:gap-8">
            <article
              data-pricing-plan
              className="flex min-w-0 flex-col overflow-hidden rounded-2xl border border-primary/40 bg-foreground p-6 text-background shadow-sm sm:p-8 lg:p-10 xl:p-12"
            >
              <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.14em] text-primary">
                    Goldypaper
                  </p>
                  <h3 className="mt-3 min-w-0 font-serif text-4xl font-semibold leading-tight tracking-tight [overflow-wrap:anywhere] sm:text-5xl">
                    Per invitation
                  </h3>
                  <div className="mt-6 flex flex-wrap items-end gap-x-4 gap-y-2 font-serif tabular-nums">
                    <span
                      data-regular-price
                      className="pb-2 text-3xl font-medium leading-none tracking-tight text-background/50 line-through decoration-2 sm:text-4xl"
                    >
                      US$39
                    </span>
                    {price ? (
                      <span
                        data-price
                        className="text-7xl font-semibold leading-none tracking-tight sm:text-8xl"
                      >
                        {price}
                      </span>
                    ) : null}
                    <span className="pb-2 text-2xl font-semibold leading-none tracking-tight text-background/90 sm:text-3xl">
                      one time
                    </span>
                  </div>
                  <p className="mt-3 font-sans text-3xl font-bold uppercase tracking-tight text-primary sm:text-4xl">
                    51% off
                  </p>
                  <p className="mt-2 font-sans text-sm text-background/70">
                    Launch price — here for a limited time.
                  </p>
                </div>
              </div>

              <p className="mt-7 border-t border-background/20 pt-6 text-lg font-medium">
                One-time purchase. No subscription.
              </p>

              <ul className="mt-6 grid gap-x-10 gap-y-4 text-base leading-relaxed text-background/75 sm:grid-cols-2">
                <li className="flex gap-3">
                  <span aria-hidden className="mt-[0.6em] size-2 shrink-0 rounded-full bg-primary" />
                  <span>Pick any theme and publish your invitation.</span>
                </li>
                <li className="flex gap-3">
                  <span aria-hidden className="mt-[0.6em] size-2 shrink-0 rounded-full bg-primary" />
                  <span>Unlimited guests — no charge per guest.</span>
                </li>
                <li className="flex gap-3 sm:col-span-2">
                  <span aria-hidden className="mt-[0.6em] size-2 shrink-0 rounded-full bg-primary" />
                  <span>
                    Guest RSVPs, countdown, venue map, and share-ready pages are all
                    included.
                  </span>
                </li>
              </ul>

              <div className="mt-7 border-t border-background/20 pt-6">
                <p className="max-w-2xl text-lg leading-relaxed">
                  <strong className="font-semibold">Your data stays yours.</strong>{" "}
                  <span className="text-background/75">Download it or delete it any time.</span>
                </p>
              </div>

              <div className="mt-auto pt-8">
                <Button
                  asChild
                  size="lg"
                  data-pricing-cta
                  className="h-12 rounded-full bg-background px-8 font-semibold text-foreground hover:bg-background/90"
                >
                  <a href={signUpPath}>Start your invitation</a>
                </Button>
              </div>
            </article>

            <aside
              data-custom-plan
              aria-labelledby="custom-plan-heading"
              className="flex min-w-0 flex-col rounded-xl border border-border bg-muted/60 p-6 text-foreground sm:p-8 lg:p-10"
            >
              <h3
                id="custom-plan-heading"
                className="min-w-0 font-serif text-3xl font-semibold leading-tight tracking-tight [overflow-wrap:anywhere]"
              >
                Custom
              </h3>

              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                For a custom invitation design, a custom domain, or both, tell us what you
                have in mind.
              </p>

              <div className="mt-auto border-t border-border pt-6">
                <p className="text-sm text-muted-foreground">Say hello</p>
                <a
                  href="mailto:contact@goldypaper.com"
                  className="mt-2 inline-flex min-h-11 items-center gap-2 whitespace-nowrap rounded-sm text-base font-semibold text-foreground underline decoration-primary/70 underline-offset-4 transition-colors hover:text-foreground/75 active:text-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  contact@goldypaper.com
                  <ArrowUpRightIcon aria-hidden className="size-4 shrink-0 text-primary" />
                </a>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </main>
  );
}
